import { DialogActions } from '../primitives/dialog.js'
import { Button } from '../primitives/button.js'
import { Typography } from '../primitives/typography.js'
import type { DefaultValues } from '../autoform/index.js'
import { AutoForm } from '../autoform/index.js'
import type { $ZodType } from 'zod/v4/core'
import { safeParse } from 'zod/v4/core'
import { useCallback, useEffect, useState } from 'react'
import type { CDialogProps } from './dialog.js'
import { Dialog } from './dialog.js'

interface FormDialogProps<T> {
  /** `description` renders above the form; `okText` labels the confirm button (default "OK") */
  dialogProps: Omit<CDialogProps, 'onClose' | 'open'> & { description?: string; okText?: string }
  open: boolean
  schema: $ZodType<T>
  defaultValues?: DefaultValues<T>
  onChange(value: T): void
  onClose(success: boolean): void
}

export const FormDialog = <T extends Record<string, unknown> = Record<string, unknown>>({
  open,
  schema,
  dialogProps,
  defaultValues,
  onChange,
  onClose,
}: FormDialogProps<T>) => {
  /**
   * A schema that already accepts `{}` — a confirmation dialog's empty `z.object({})`, or a form
   * whose every field is optional — needs nothing typed into it, so it is valid on open.
   */
  const completeOnOpen = safeParse(schema, {}).success
  const [valid, setValid] = useState(Boolean(defaultValues) || completeOnOpen)
  const handleChange = useCallback(
    (val: T) => {
      setValid(true)
      onChange(val)
    },
    [onChange]
  )

  /**
   * AutoForm only reports a value when a FIELD changes, so a fieldless form would resolve the
   * caller's promise as `null` — which every call site reads as "cancelled", silently dropping a
   * confirmed action. Report the empty value once so confirming means confirmed.
   */
  useEffect(() => {
    if (open && completeOnOpen && !defaultValues) {
      onChange({} as T)
    }
  }, [completeOnOpen, defaultValues, onChange, open])
  const handleClose = useCallback(
    (success: boolean) => () => {
      onClose(success)
    },
    [onClose]
  )

  const handleError = useCallback(() => {
    setValid(false)
  }, [])

  const { description, okText = 'OK', ...restDialogProps } = dialogProps

  return (
    <Dialog open={open} onClose={handleClose(false)} showClose={false} showOK={false} {...restDialogProps}>
      {description ? <Typography variant="body2">{description}</Typography> : null}
      <AutoForm defaultValues={defaultValues} schema={schema} onSubmit={handleChange} onError={handleError} />

      <DialogActions>
        <Button disabled={!valid} onClick={handleClose(true)}>
          {okText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
